<template>
  <v-card>
    <div class="flex-align pl-4 py-2 pr-2 justify-between">
      <h2>Today</h2>
      <v-btn
        :to="{ name: 'settings-payouts' }"
        class="text-none"
        elevation="1"
        text="Pay out funds"
      />
    </div>
    <v-divider />
    <div class="pa-4 flex gap-2">
      <div class="md:w-200px">
        <TextSelect v-model="activeChart" :items="metrics" />
        <div class="text-5.5 font-semibold">
          <MetricValue source="valueResult" default="0" with-unit />
        </div>
      </div>
      <div class="md:w-200px">
        <MetricsDatePicker v-model="comparison">
          <template #activator="{ props, display }">
            <v-btn v-bind="props" size="x-small" class="ml--2 text-3.5">
              <span class="mr-1">{{ display }}</span>
              <Icon name="ic:baseline-expand-more" />
            </v-btn>
          </template>
        </MetricsDatePicker>
        <div class="text-5.5 font-light">
          <MetricValue source="valueResult" dataset="1" default="0" with-unit />
        </div>
      </div>
    </div>
    <ContentLoader :loading="loading">
      <div class="px-2">
        <MetricsChart source="trendResult" height="200" width="100%" />
      </div>
      <div class="pb-2 px-4 flex justify-between text-3">
        <span class="font-semibold">{{ since }}</span>
        <span>{{ until }}</span>
      </div>
    </ContentLoader>
    <v-divider />
    <div
      class="highlight md:grid"
      :class="{
        'md:grid-cols-2': !hasPendingTransfers,
        'md:grid-cols-3': hasPendingTransfers,
      }"
    >
      <div class="pa-4">
        <div class="flex justify-between">
          <span class="heading-2">{{ balanceCurrency }} balance</span>
          <router-link :to="{ name: 'settings-payouts' }" class="link text-3">
            View
          </router-link>
        </div>
        <div class="text-5 font-light">
          <TextLoader :loading="loadingBalance" :text="availableBalance" />
        </div>
        <div class="caption">
          Available to pay out (<TextLoader
            :loading="loadingBalance"
            :text="instantAvailableBalance"
          />
          available instantly)
        </div>
      </div>
      <div class="pa-4">
        <div class="heading-2">Pending balance</div>
        <div class="text-5 font-light">
          <TextLoader :loading="loadingBalance" :text="pendingBalance" />
        </div>
        <div class="caption">On its way to your account</div>
      </div>
      <div v-if="hasPendingTransfers" class="pa-4">
        <div class="heading-2">Pending transfers</div>
        <div class="text-5 font-light">
          <TextLoader :text="pendingTransfers" />
        </div>
        <div class="caption">
          Pending sales not yet added to your available or pending balance.
        </div>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  MetricAggregateFunction,
  type MetricsControls,
} from "~/layers/admin/modules/metrics/runtime/types";

export default defineComponent({
  setup() {
    const metrics = [
      { title: "Total sales", value: "sales", metric: { name: "orders" } },
      {
        title: "New customers",
        value: "customers",
        metric: { name: "customers", aggregate: MetricAggregateFunction.COUNT },
      },
      // {
      //   title: "Event tickets",
      //   value: "tickets",
      //   metric: { name: "event_tickets" },
      // },
    ];

    const { activeChart } = storeToRefs(useHomePageStore());
    const { comparison } = storeToRefs(useAnalyticsStore());

    const {
      loading: loadingBalance,
      available: availableBalance,
      instantAvailable: instantAvailableBalance,
      pending: pendingBalance,
      pendingTransfers,
      currency: balanceCurrency,
      pendingTransfersRaw,
    } = useBalances();

    const hasPendingTransfers = computed(() => pendingTransfersRaw.value > 0);

    const sources = computed(() => {
      const selection = findWhere(metrics, { value: activeChart.value })!;

      return defineDbMetricsSources({
        valueResult: {
          name: selection.metric.name,
          aggregate: "count",
          type: "value",
        },
        trendResult: selection.metric,
      });
    });

    const since = now().startOf("day");
    const until = now();

    const controls = computed(
      (): MetricsControls => ({
        comparisons: comparison.value ? [comparison.value] : [],
        since: formatToDatetime(since),
        until: formatToDatetime(until),
      })
    );

    const { loading, getters } = provideMetrics(sources, controls);
    const value = getters.value("valueResult");

    return {
      metrics,
      activeChart,
      comparison,
      loadingBalance,
      availableBalance,
      instantAvailableBalance,
      pendingBalance,
      pendingTransfers,
      balanceCurrency,
      loading,
      hasPendingTransfers,
      value,
      since: since.format("h:mm a"),
      until: `Now, ` + until.format("h:mm a"),
    };
  },
});
</script>
